import React from 'react'
import { DayResult } from '../types'
import { buildMonthWeeks } from '../lib/calendarUtils'
import { MONTH_NAMES, WEEKDAY_LABELS, describeDay, formatMoney, formatMoneyCompact, formatSigned } from '../lib/format'
import { ragBg, ragText, targetClasses, targetReplacesBg } from '../lib/ragStyles'
import { toISODate } from '../lib/dateRules'

interface Props {
  year: number
  month0: number
  results: Map<string, DayResult>
  currencySymbol: string
  onSelectDay: (iso: string) => void
  todayIso: string
}

export default function MonthView({ year, month0, results, currencySymbol, onSelectDay, todayIso }: Props) {
  const weeks = buildMonthWeeks(year, month0)

  return (
    <div className="bg-white border border-line rounded">
      {/* The header already names the month on a phone; repeating it here
          costs a row of calendar. */}
      <div className="hidden sm:block px-4 py-3 border-b border-line">
        <h2 className="font-display text-xl text-ink">
          {MONTH_NAMES[month0]} {year}
        </h2>
      </div>
      <div className="grid grid-cols-7 border-b border-line">
        {WEEKDAY_LABELS.map((w) => (
          <div key={w} className="text-[10px] sm:text-xs font-sans text-ink/70 text-center py-1.5 sm:py-2">
            <span className="sm:hidden">{w[0]}</span>
            <span className="hidden sm:inline">{w}</span>
          </div>
        ))}
      </div>
      <div className="divide-y divide-line">
        {weeks.map((week, wi) => (
          <div key={wi} className="grid grid-cols-7 divide-x divide-line">
            {week.map((day, di) => {
              if (!day) return <div key={di} className="min-h-[max(58px,11dvh)] sm:min-h-[92px] bg-paper/40" />
              const iso = toISODate(day)
              const result = results.get(iso)
              const isToday = iso === todayIso
              const hit = result?.targetHits[0]
              return (
                <button
                  key={di}
                  aria-label={describeDay(day, result, currencySymbol)}
                  disabled={!result}
                  onClick={result ? () => onSelectDay(iso) : undefined}
                  title={
                    result
                      ? hit
                        ? `${hit.name} — ${formatMoney(hit.level, currencySymbol)} reached`
                        : undefined
                      : `${iso}: before starting balance date`
                  }
                  className={`min-h-[max(58px,11dvh)] sm:min-h-[92px] p-1 sm:p-2 text-left flex flex-col justify-between overflow-hidden transition-colors
                    ${!result ? 'bg-line/40 cursor-not-allowed' : hit && targetReplacesBg(hit.style) ? '' : ragBg[result.rag]}
                    ${hit ? targetClasses(hit.style) : ''}
                    ${result ? 'hover:brightness-95' : ''} ${isToday ? 'ring-1 ring-inset ring-accent' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`text-[10px] sm:text-xs font-mono ${result ? 'text-ink/75' : 'text-ink/25'}`}>
                      {day.getDate()}
                    </span>
                    {result && result.transactions.length > 0 && (
                      // A dot on a phone, the count once the cell is wide
                      // enough for a second character not to crowd the date.
                      <>
                        <span className="sm:hidden w-1 h-1 rounded-full bg-ink/35" aria-hidden />
                        <span className="hidden sm:inline text-[10px] font-mono text-ink/70">
                          {result.transactions.length}
                        </span>
                      </>
                    )}
                  </div>
                  {result && (
                    <div className="min-w-0">
                      <div
                        className={`text-[10px] sm:text-sm font-mono font-medium font-tabular leading-tight truncate ${ragText[result.rag]}`}
                      >
                        <span className="sm:hidden">{formatMoneyCompact(result.balance, currencySymbol)}</span>
                        <span className="hidden sm:inline">{formatMoney(result.balance, currencySymbol)}</span>
                      </div>
                      {result.netChange !== 0 && (
                        // Dropped on a phone: at ~52px per cell the balance is
                        // what people are scanning for, and a second number
                        // under it just makes both harder to read.
                        <div className="hidden sm:block text-[10px] font-mono text-ink/70 font-tabular">
                          {formatSigned(result.netChange, currencySymbol)}
                        </div>
                      )}
                    </div>
                  )}
                </button>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}
